/**
 * BrandingProvider -- fetches branding on boot and applies it.
 *
 * Placed in the provider tree ABOVE AuthHydrator and Router so that
 * even unauthenticated/public pages (login, register) get branded.
 * Must be INSIDE QueryClientProvider (uses TanStack Query).
 *
 * On mount:
 * 1. Checks localStorage cache for immediate FOUC prevention (already handled
 *    by the inline script in index.html, but also ensures store is seeded).
 * 2. Fetches GET /api/v1/branding (no auth required).
 * 3. On success: updates useBrandingStore, calls applyBranding(), and
 *    optionally sets the default theme if no user preference exists.
 *
 * The provider does NOT block rendering -- branding is applied as soon as
 * it resolves, and the FOUC inline script covers the gap.
 */

import { useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPublicBranding } from '@/services/branding';
import { useBrandingStore } from '@/stores/useBrandingStore';
import { applyBranding, getCachedBranding } from '@/utils/brandTheme';
import { BRANDING_DEFAULTS } from '@/config/brandingDefaults';
import { useThemeStore } from '@/stores/useThemeStore';

interface BrandingProviderProps {
  children: ReactNode;
}

const THEME_STORAGE_KEY = 'opus-ia-theme';

export function BrandingProvider({ children }: BrandingProviderProps) {
  const setBranding = useBrandingStore((s) => s.setBranding);
  const setLoaded = useBrandingStore((s) => s.setLoaded);

  // Seed store from localStorage cache immediately (synchronous)
  useEffect(() => {
    const cached = getCachedBranding();
    if (cached) {
      setBranding(cached);
      // Apply cached branding right away (complements FOUC script)
      applyBranding(cached);
    }
  }, []);

  // Fetch branding from server (public, no auth)
  const { data } = useQuery({
    queryKey: ['branding'],
    queryFn: getPublicBranding,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: 1000,
  });

  // Apply server branding when it arrives
  useEffect(() => {
    if (!data) return;

    const branding = {
      platform_name: data.platform_name || BRANDING_DEFAULTS.platform_name,
      logo_url: data.logo_url ?? BRANDING_DEFAULTS.logo_url,
      primary_color: data.primary_color || BRANDING_DEFAULTS.primary_color,
      secondary_color: data.secondary_color || BRANDING_DEFAULTS.secondary_color,
      default_theme: data.default_theme || BRANDING_DEFAULTS.default_theme,
    };

    setBranding(branding);
    setLoaded();
    applyBranding(branding);

    // Apply default theme only if user has NO stored preference
    applyDefaultTheme(branding.default_theme);
  }, [data]);

  return <>{children}</>;
}

/**
 * Apply the org's default theme ONLY when the user has no explicit stored preference.
 * This ensures first-time visitors get the org default but returning users keep theirs.
 */
function applyDefaultTheme(orgDefault: 'light' | 'dark' | 'system'): void {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored) {
      // User already has a preference -- respect it
      return;
    }
    // No stored preference -- apply org default
    useThemeStore.getState().setTheme(orgDefault);
  } catch {
    // localStorage unavailable -- non-critical
  }
}
